// Acquisition is captured on a session's landing page, and again whenever a
// later page arrives from a campaign, then read back at signup (stored on the
// Shopify customer) and at checkout (stamped on the order).

export const ACQUISITION_STORAGE_KEY = "matic_acquisition";

export const ACQUISITION_MAX_LEN = 512;

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

// The ad platforms' auto-tagging parameters, which mark paid clicks that
// carry no UTMs. Only the name is kept; each platform's own tag reads the value.
const AD_CLICK_PARAMS = [
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "ttclid",
  "twclid",
  "irclickid",
  "oppref",
] as const;

export const ACQUISITION_KEYS = [
  ...UTM_KEYS,
  "referrer",
  "landing_page",
  "signup_page",
  "signup_widget",
] as const;

export type SignupWidget =
  "sweepstakes" | "email_toast" | "stay_updated" | "lp_offer" | "wsj";

export type Acquisition = Partial<Record<(typeof UTM_KEYS)[number], string>> & {
  referrer?: string;
  landing_page?: string;
  ad_click?: string;
  signup_page?: string;
  signup_widget?: SignupWidget;
};

export function captureAcquisition(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const adClick = AD_CLICK_PARAMS.find((param) => params.has(param));
    const fromCampaign =
      adClick !== undefined || UTM_KEYS.some((key) => params.has(key));
    if (!fromCampaign && sessionStorage.getItem(ACQUISITION_STORAGE_KEY)) {
      return;
    }
    const data: Acquisition = {};
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) data[key] = value.slice(0, ACQUISITION_MAX_LEN);
    }
    if (adClick !== undefined) data.ad_click = adClick;
    if (
      document.referrer &&
      !document.referrer.startsWith(window.location.origin)
    ) {
      data.referrer = document.referrer.slice(0, ACQUISITION_MAX_LEN);
    }
    data.landing_page = window.location.pathname.slice(0, ACQUISITION_MAX_LEN);
    sessionStorage.setItem(ACQUISITION_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Failed to capture acquisition:", error);
  }
}

export function readAcquisition(): Acquisition {
  try {
    return JSON.parse(sessionStorage.getItem(ACQUISITION_STORAGE_KEY) ?? "{}");
  } catch (error) {
    console.error("Failed to read acquisition:", error);
    return {};
  }
}

export function getAcquisition(signupWidget: SignupWidget): Acquisition {
  return {
    ...readAcquisition(),
    signup_page: window.location.pathname.slice(0, ACQUISITION_MAX_LEN),
    signup_widget: signupWidget,
  };
}
